import { createContext, useContext, useReducer, ReactNode, useEffect, useCallback } from 'react';
import produce from 'immer';
import { cloneDeep } from 'lodash';

import { useInsightsContext } from './InsightsContext';
import { useMainTableContext } from './MainTableContext';
import { unimplemented } from '@utils/commonUtils';
import { CategoryType, GarmRiskLevelType } from '@insights_module/types/BrandSafety';
import { EpisodeSortType } from '@insights_module/types/Episode';
import { SORT_ORDER, EPISODES_SORT_KEY } from '@insights_module/constants/defaults';
import {
  AudioContextualSummary,
  GroupLinkedNamedEntity,
} from '@insights_module/types/graphql/Schema';
import { GARM_CATEGORIES } from '@insights_module/constants/garm';

type ShowDetailsContextKeys =
  | 'insights.show_details.show_side_bar'
  | 'insights.show_details.show_artwork'
  | 'insights.show_details.selected_episodes_table_columns'
  | 'insights.show_details.selected_date_column'
  | 'insights.show_details.is_drawer_expanded'
  | 'insights.show_details.selected_garm_category'
  | 'insights.show_details.selected_garm_risk'
  | 'insights.show_details.selected_entity'
  | 'insights.show_details.selected_category'
  | 'insights.show_details.show_last_90_days'
  | 'insights.show_details.episodes_related_to_garm_count'
  | 'insights.show_details.garm_score_date'
  | 'insights.show_details.search_term'
  | 'insights.show_details.sort'
  | 'insights.show_details.page_number'
  | 'insights.show_details.table_view'
  | 'insights.show_details.columns';

export type ColumnDateType = 'processed' | 'published';

export type ShowDetailsTableView = 'info' | 'brand_safety_garm' | 'contextual' | 'entities';

type Action = {
  type: ShowDetailsContextKeys;
  payload:
    | number
    | string
    | string[]
    | boolean
    | null
    | CategoryType
    | GarmRiskLevelType
    | AudioContextualSummary
    | EpisodeSortType
    | ShowDetailsTableView
    | GroupLinkedNamedEntity
    | ShowDetailsEpisodesTableColumns;
};

export interface Column {
  key: string;
  align: 'left' | 'center' | 'right';
  isVisible: boolean;
}

export interface ShowDetailsEpisodesTableColumns {
  info: Column[];
  brand_safety_garm: Column[];
  contextual: Column[];
  entities: Column[];
}

export interface IShowDetailsContext {
  showSideBar: boolean;
  showArtwork: boolean;
  selectedEpisodesTableColumns: ShowDetailsEpisodesTableColumns;
  selectedDateColumn: ColumnDateType | null;
  selectedShowDetailsGarmCategory: CategoryType | string | null;
  selectedShowDetailsGarmRisk: GarmRiskLevelType | GarmRiskLevelType[] | null;
  selectedShowDetailsEntity: GroupLinkedNamedEntity | null;
  selectedShowDetailsCategory: AudioContextualSummary | null;
  showLast90Days: boolean;
  episodeRelatedToGarmScoreCount: number;
  garmScoreDate: string | null;
  searchTerm: string;
  sort: EpisodeSortType;
  pageNumber: string;
  tableView: ShowDetailsTableView;
  setShowSideBar: (value: boolean) => void;
  setShowArtwork: (value: boolean) => void;
  setSelectedEpisodesTableColumns: (value: ShowDetailsEpisodesTableColumns) => void;
  toggleColumnVisibility: (columnKey: string) => void;
  setSelectedDateColumn: (value: ColumnDateType | null) => void;
  setSelectedShowDetailsGarmCategory: (val: CategoryType | null) => void;
  setSelectedShowDetailsGarmRisk: (
    val: GarmRiskLevelType | GarmRiskLevelType[] | string[] | null
  ) => void;
  setSelectedShowDetailNamedEntity: (val: GroupLinkedNamedEntity | null) => void;
  setSelectedShowDetailsCategory: (val: AudioContextualSummary | null) => void;
  setShowLast90Days: (val: boolean) => void;
  setEpisodeRelatedToGarmScoreCount: (val: number) => void;
  setGarmScoreDate: (val: string | null) => void;
  getGridTemplateEpisodesTableColumns: (isHeader?: boolean) => string;
  setSearchTerm: (val: string) => void;
  setSort: (params: EpisodeSortType) => void;
  setPageNumber: (pageNumber: string) => void;
  setTableView: (tableView: ShowDetailsTableView) => void;
  setColumns: (columns: string[]) => void;
}

const SHOW_DETAILS_CONTEXT_KEYS = {
  SHOW_SIDE_BAR: 'insights.show_details.show_side_bar' as ShowDetailsContextKeys,
  SHOW_ARTWORK: 'insights.show_details.show_artwork' as ShowDetailsContextKeys,
  SELECTED_EPISODES_TABLE_COLUMNS:
    'insights.show_details.selected_episodes_table_columns' as ShowDetailsContextKeys,
  SELECTED_DATE_COLUMN: 'insights.show_details.selected_date_column' as ShowDetailsContextKeys,
  IS_DRAWER_EXPANDED: 'insights.show_details.is_drawer_expanded' as ShowDetailsContextKeys,
  SELECTED_GARM_CATEGORY: 'insights.show_details.selected_garm_category' as ShowDetailsContextKeys,
  SELECTED_GARM_RISK: 'insights.show_details.selected_garm_risk' as ShowDetailsContextKeys,
  SELECTED_ENTITY: 'insights.show_details.selected_entity' as ShowDetailsContextKeys,
  SELECTED_CATEGORY: 'insights.show_details.selected_category' as ShowDetailsContextKeys,
  SHOW_LAST_90_DAYS: 'insights.show_details.show_last_90_days' as ShowDetailsContextKeys,
  EPISODES_RELATED_TO_GARM_COUNT:
    'insights.show_details.episodes_related_to_garm_count' as ShowDetailsContextKeys,
  GARM_SCORE_DATE: 'insights.show_details.garm_score_date' as ShowDetailsContextKeys,
  SEARCH_TERM: 'insights.show_details.searh_term' as ShowDetailsContextKeys,
  SORT: 'insights.show_details.sort' as ShowDetailsContextKeys,
  PAGE_NUMBER: 'insights.show_details.page_number' as ShowDetailsContextKeys,
  TABLE_VIEW: 'insights.show_details.table_view' as ShowDetailsContextKeys,
};

export const TABLE_VIEW = {
  INFO: 'info',
  GARM: 'brand_safety_garm',
  CONTEXTUAL: 'contextual',
  ENTITIES: 'entities',
};

export const EPISODES_TABLE_COLUMNS = {
  ARTWORK: 'artwork',
  TITLE: 'title',
  DATE: 'date',
  GARM: 'garm',
};

export const COLUMN_DATE_TYPE = {
  PROCESSED: 'processed' as ColumnDateType,
  PUBLISHED: 'published' as ColumnDateType,
};

const SHOW_DETAILS_EPISODES_TABLE_COLUMNS_STORAGE_KEY_OLD = 'show-details-episodes-table-columns';
const SHOW_DETAILS_EPISODES_TABLE_COLUMNS_STORAGE_KEY = 'show-details-episodes-table-columns-v2';

export const SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS = {
  EPISODES: 'episodes',
  PUBLISHED_DATE: 'published_date',
  DURATION: 'duration',

  ADULT_EXPLICIT_CONTENT: 'adult_explicit_content',
  ARMS_AMUNIION: 'arms_ammunition',
  CRIME_HARMFUL_ACT: 'crime_harmful_act',
  DEATH_INJURY_MILITARY_CONFLICT: 'death_injury_military_conflict',
  DEBATED_SOCIAL: 'debated_social',
  HATE_SPEECH: 'hate_speech',
  ILLEGAL_DRUGS: 'illegal_drugs',
  OBSCENITY: 'obscenity',
  ONLINE_PIRACY: 'online_piracy',
  SPAM_HARMFUL: 'spam_harmful',
  TERRORISM: 'terrorism',
  ADULT_CONTENT: 'adult_content',
  ARMS: 'arms',
  CRIME: 'crime',
  DEATH_INJURY: 'death_injury',
  DRUGS: 'drugs',
  MILITARY_CONFLICT: 'military_conflict',
  TABACOO: 'tobacco',

  CONTEXTUAL: 'contextual',

  ENTITIES: 'entities',
};

const showDetailsEpisodesTableColumns: ShowDetailsEpisodesTableColumns = {
  info: [
    {
      key: SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.EPISODES,
      align: 'left',
      isVisible: true,
    },
    {
      key: SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.PUBLISHED_DATE,
      align: 'left',
      isVisible: true,
    },
    {
      key: SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.DURATION,
      align: 'left',
      isVisible: true,
    },
  ],
  brand_safety_garm: [
    {
      key: SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.EPISODES,
      align: 'left',
      isVisible: true,
    },
    {
      key: SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.PUBLISHED_DATE,
      align: 'left',
      isVisible: true,
    },
    ...Object.values(GARM_CATEGORIES).map((category) => ({
      key: category as string,
      align: 'left' as 'left' | 'center' | 'right',
      isVisible: true,
    })),
  ],
  contextual: [
    {
      key: SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.EPISODES,
      align: 'left',
      isVisible: true,
    },
    {
      key: SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.PUBLISHED_DATE,
      align: 'left',
      isVisible: true,
    },
    {
      key: SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.CONTEXTUAL,
      align: 'left',
      isVisible: true,
    },
  ],
  entities: [
    {
      key: SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.EPISODES,
      align: 'left',
      isVisible: true,
    },
    {
      key: SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.PUBLISHED_DATE,
      align: 'left',
      isVisible: true,
    },
    {
      key: SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.ENTITIES,
      align: 'left',
      isVisible: true,
    },
  ],
};

// removing old localStorage key data
// TODO: remove this after a month of releasing the new version
const oldLocalStorageData: string | null = window.localStorage.getItem(
  SHOW_DETAILS_EPISODES_TABLE_COLUMNS_STORAGE_KEY_OLD
);
if (oldLocalStorageData) {
  localStorage.removeItem(SHOW_DETAILS_EPISODES_TABLE_COLUMNS_STORAGE_KEY_OLD);
}

const localStorageData: string | null = window.localStorage.getItem(
  SHOW_DETAILS_EPISODES_TABLE_COLUMNS_STORAGE_KEY
);

const initialState = {
  showSideBar: true,
  showArtwork: true,
  selectedEpisodesTableColumns: localStorageData
    ? (JSON.parse(localStorageData) as ShowDetailsEpisodesTableColumns)
    : showDetailsEpisodesTableColumns,
  selectedDateColumn: 'published' as ColumnDateType,
  selectedShowDetailsGarmCategory: null,
  selectedShowDetailsGarmRisk: null,
  selectedShowDetailsEntity: null,
  selectedShowDetailsCategory: null,
  showLast90Days: false,
  episodeRelatedToGarmScoreCount: 0,
  garmScoreDate: null,
  searchTerm: '',
  sort: {
    sort_field: EPISODES_SORT_KEY.PUBLISHED_DATE,
    sort_order: SORT_ORDER.DESC,
  },
  pageNumber: '1',
  tableView: 'info' as ShowDetailsTableView,
  setShowSideBar: unimplemented,
  setShowArtwork: unimplemented,
  setSelectedEpisodesTableColumns: unimplemented,
  toggleColumnVisibility: unimplemented,
  setSelectedDateColumn: unimplemented,
  setSelectedShowDetailsGarmCategory: unimplemented,
  setSelectedShowDetailsGarmRisk: unimplemented,
  setSelectedShowDetailNamedEntity: unimplemented,
  setSelectedShowDetailsCategory: unimplemented,
  setShowLast90Days: unimplemented,
  setEpisodeRelatedToGarmScoreCount: unimplemented,
  setGarmScoreDate: unimplemented,
  getGridTemplateEpisodesTableColumns: () => '',
  setSearchTerm: unimplemented,
  setSort: unimplemented,
  setPageNumber: unimplemented,
  setTableView: unimplemented,
  setColumns: unimplemented,
};

const ShowDetailsContext = createContext<IShowDetailsContext>(initialState);

const reducer = produce((draft: IShowDetailsContext, action: Action) => {
  const { type, payload } = action;
  switch (type) {
    case SHOW_DETAILS_CONTEXT_KEYS.SHOW_SIDE_BAR:
      draft.showSideBar = payload as boolean;
      break;

    case SHOW_DETAILS_CONTEXT_KEYS.SHOW_ARTWORK:
      draft.showArtwork = payload as boolean;
      break;

    case SHOW_DETAILS_CONTEXT_KEYS.SELECTED_EPISODES_TABLE_COLUMNS:
      draft.selectedEpisodesTableColumns = payload as ShowDetailsEpisodesTableColumns;
      localStorage.setItem(
        SHOW_DETAILS_EPISODES_TABLE_COLUMNS_STORAGE_KEY,
        JSON.stringify(draft.selectedEpisodesTableColumns)
      );
      break;

    case SHOW_DETAILS_CONTEXT_KEYS.SELECTED_DATE_COLUMN:
      draft.selectedDateColumn = payload as ColumnDateType;
      break;

    case SHOW_DETAILS_CONTEXT_KEYS.SELECTED_GARM_CATEGORY: {
      draft.selectedShowDetailsEntity = null;
      draft.selectedShowDetailsCategory = null;
      draft.selectedShowDetailsGarmCategory = payload as CategoryType | null;
      break;
    }

    case SHOW_DETAILS_CONTEXT_KEYS.SELECTED_GARM_RISK: {
      draft.selectedShowDetailsEntity = null;
      draft.selectedShowDetailsCategory = null;
      draft.selectedShowDetailsGarmRisk = payload as GarmRiskLevelType | GarmRiskLevelType[] | null;
      break;
    }

    case SHOW_DETAILS_CONTEXT_KEYS.SELECTED_ENTITY: {
      draft.selectedShowDetailsGarmCategory = null;
      draft.selectedShowDetailsGarmRisk = null;
      draft.selectedShowDetailsCategory = null;
      draft.selectedShowDetailsEntity = payload as GroupLinkedNamedEntity | null;
      break;
    }

    case SHOW_DETAILS_CONTEXT_KEYS.SELECTED_CATEGORY: {
      draft.selectedShowDetailsGarmCategory = null;
      draft.selectedShowDetailsGarmRisk = null;
      draft.selectedShowDetailsEntity = null;
      draft.selectedShowDetailsCategory = payload as AudioContextualSummary;
      break;
    }

    case SHOW_DETAILS_CONTEXT_KEYS.SHOW_LAST_90_DAYS:
      draft.showLast90Days = payload as boolean;
      break;

    case SHOW_DETAILS_CONTEXT_KEYS.EPISODES_RELATED_TO_GARM_COUNT:
      draft.episodeRelatedToGarmScoreCount = payload as unknown as number;
      break;

    case SHOW_DETAILS_CONTEXT_KEYS.GARM_SCORE_DATE:
      draft.garmScoreDate = payload as string | null;
      break;

    case SHOW_DETAILS_CONTEXT_KEYS.SEARCH_TERM:
      draft.searchTerm = payload as string;
      break;

    case SHOW_DETAILS_CONTEXT_KEYS.SORT:
      draft.sort = payload as EpisodeSortType;
      break;

    case SHOW_DETAILS_CONTEXT_KEYS.PAGE_NUMBER:
      draft.pageNumber = payload as string;
      break;

    case SHOW_DETAILS_CONTEXT_KEYS.TABLE_VIEW:
      draft.tableView = payload as ShowDetailsTableView;
      break;

    default:
      break;
  }
}, initialState);

export const ShowDetailsProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { selectedShow } = useInsightsContext();
  const { garmColumns } = useMainTableContext();
  const { tableView, selectedEpisodesTableColumns } = state;

  const getGridTemplateEpisodesTableColumns = useCallback(
    (isHeader = false) => {
      let rowTitleColumn = '300px';
      let headerTitleColumn = '';
      let eyeColumn = '';
      let dateColumn = '';

      if (state.selectedDateColumn) {
        dateColumn = '140px';
      }
      if (isHeader) {
        headerTitleColumn = '252px';
        eyeColumn = '48px';
        rowTitleColumn = '';

        !state.selectedDateColumn && (dateColumn = '30px');
      }

      const garmColumnsNumber = Object.values(garmColumns).filter(
        (showColumn) => showColumn
      ).length;

      const garmColumnsTemplate = garmColumnsNumber
        ? `repeat(${garmColumnsNumber.toString()}, minmax(106px, 200px))`
        : '';

      return `grid-template-columns: ${eyeColumn} ${headerTitleColumn} ${rowTitleColumn} ${dateColumn} ${garmColumnsTemplate}`;
    },
    [state.selectedDateColumn, garmColumns]
  );

  useEffect(() => {
    // reset all filters when table view changes
    dispatch({ type: SHOW_DETAILS_CONTEXT_KEYS.SELECTED_GARM_CATEGORY, payload: null });
    dispatch({ type: SHOW_DETAILS_CONTEXT_KEYS.SELECTED_GARM_RISK, payload: null });
    dispatch({ type: SHOW_DETAILS_CONTEXT_KEYS.SELECTED_ENTITY, payload: null });
    dispatch({ type: SHOW_DETAILS_CONTEXT_KEYS.SELECTED_CATEGORY, payload: null });
    dispatch({ type: SHOW_DETAILS_CONTEXT_KEYS.SHOW_LAST_90_DAYS, payload: false });
  }, [tableView]);

  const toggleColumnVisibility = (columnKey: string) => {
    const columns = cloneDeep(selectedEpisodesTableColumns);
    const column = columns[tableView as keyof ShowDetailsEpisodesTableColumns].find(
      (col) => col.key === columnKey
    );
    if (column) {
      column.isVisible = !column.isVisible;
      dispatch({
        type: SHOW_DETAILS_CONTEXT_KEYS.SELECTED_EPISODES_TABLE_COLUMNS,
        payload: columns,
      });
    }
  };

  const setShowSideBar = (value: boolean) => {
    dispatch({ type: SHOW_DETAILS_CONTEXT_KEYS.SHOW_SIDE_BAR, payload: value });
  };

  const setShowArtwork = (value: boolean) => {
    dispatch({ type: SHOW_DETAILS_CONTEXT_KEYS.SHOW_ARTWORK, payload: value });
  };

  const setSelectedEpisodesTableColumns = (value: ShowDetailsEpisodesTableColumns) => {
    dispatch({ type: SHOW_DETAILS_CONTEXT_KEYS.SELECTED_EPISODES_TABLE_COLUMNS, payload: value });
  };

  const setSelectedDateColumn = (value: ColumnDateType | null) => {
    dispatch({ type: SHOW_DETAILS_CONTEXT_KEYS.SELECTED_DATE_COLUMN, payload: value });
  };

  const setSelectedShowDetailsGarmCategory = (value: string | null) => {
    dispatch({ type: SHOW_DETAILS_CONTEXT_KEYS.SELECTED_GARM_CATEGORY, payload: value });
  };

  const setSelectedShowDetailsGarmRisk = (
    value: GarmRiskLevelType | GarmRiskLevelType[] | string[] | null
  ) => {
    dispatch({ type: SHOW_DETAILS_CONTEXT_KEYS.SELECTED_GARM_RISK, payload: value });
  };

  const setSelectedShowDetailNamedEntity = (value: GroupLinkedNamedEntity | null) => {
    dispatch({ type: SHOW_DETAILS_CONTEXT_KEYS.SELECTED_ENTITY, payload: value });
  };

  const setSelectedShowDetailsCategory = (value: AudioContextualSummary | null) => {
    dispatch({ type: SHOW_DETAILS_CONTEXT_KEYS.SELECTED_CATEGORY, payload: value });
  };

  const setShowLast90Days = (value: boolean) => {
    dispatch({ type: SHOW_DETAILS_CONTEXT_KEYS.SHOW_LAST_90_DAYS, payload: value });
  };

  const setEpisodeRelatedToGarmScoreCount = (value: number) => {
    dispatch({ type: SHOW_DETAILS_CONTEXT_KEYS.EPISODES_RELATED_TO_GARM_COUNT, payload: value });
  };

  const setGarmScoreDate = (value: string | null) => {
    dispatch({ type: SHOW_DETAILS_CONTEXT_KEYS.GARM_SCORE_DATE, payload: value });
  };

  const setSearchTerm = (value: string) => {
    dispatch({ type: SHOW_DETAILS_CONTEXT_KEYS.SEARCH_TERM, payload: value });
  };

  const setSort = (value: EpisodeSortType) => {
    dispatch({ type: SHOW_DETAILS_CONTEXT_KEYS.SORT, payload: value });
  };

  const setPageNumber = (value: string) => {
    dispatch({ type: SHOW_DETAILS_CONTEXT_KEYS.PAGE_NUMBER, payload: value });
  };

  const setTableView = (value: ShowDetailsTableView) => {
    dispatch({ type: SHOW_DETAILS_CONTEXT_KEYS.TABLE_VIEW, payload: value });
  };

  useEffect(() => {
    setSelectedShowDetailsGarmCategory(null);
    setSelectedShowDetailsGarmRisk(null);
    setSelectedShowDetailNamedEntity(null);
    setSelectedShowDetailsCategory(null);
    setShowLast90Days(false);
    setEpisodeRelatedToGarmScoreCount(0);
    setGarmScoreDate(null);
  }, [selectedShow]);

  const value = {
    ...state,
    setShowSideBar,
    setShowArtwork,
    setSelectedEpisodesTableColumns,
    setSelectedDateColumn,
    setSelectedShowDetailsGarmCategory,
    setSelectedShowDetailsGarmRisk,
    setSelectedShowDetailNamedEntity,
    setSelectedShowDetailsCategory,
    setShowLast90Days,
    setEpisodeRelatedToGarmScoreCount,
    setGarmScoreDate,
    getGridTemplateEpisodesTableColumns,
    setSearchTerm,
    setSort,
    setPageNumber,
    setTableView,
    toggleColumnVisibility,
  };

  return <ShowDetailsContext.Provider value={value}>{children}</ShowDetailsContext.Provider>;
};

export const useShowDetailsContext = () => {
  return useContext(ShowDetailsContext);
};
